import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { clearUnreadProject } from 'app/slices/dashboardSlice';
import { headerApi } from 'app/services/headerApi';
import type { IProjectNotification } from 'models';

const { useGetProjectNotificationsQuery, useMarkNotificationReadMutation } = headerApi;

/**
 * Notifications dropdown (§3.A "Notifications dropdown" / parity 112-113).
 *
 * Markup mirrors the Backbone `#notifications_wrapper` + `notifications.tmpl`:
 *   div#notifications_wrapper[.non-clickable when empty]
 *     ├── a.notifications.with-list[.active when unread]
 *     └── div#notifications_list.nav-list[.hide]
 *
 * Behavior:
 *  - List from GET /project/project_notifications/1/?page=N (50/page),
 *    infinite-scrolled (load next page within 150px of the bottom while
 *    page*50 <= loaded length).
 *  - Bell turns green (`.active`) when there is an unread row.
 *  - Empty list → the wrapper gets `.non-clickable`.
 *  - Clicking a row marks it read (PATCH is_read:true, remove .unread, recompute
 *    the active badge, and clear .unread-notification on the matching project row).
 *  - Double-clicking opens /project/<id>#project in a new tab.
 *  - Action text: critical task → "{critical_task} task started" else
 *    "Created new project".
 */
const PAGE_SIZE = 50;

interface NotificationsDropdownProps {
  open: boolean;
  onToggle: () => void;
}

const NotificationsDropdown = ({ open, onToggle }: NotificationsDropdownProps) => {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<IProjectNotification[]>([]);
  const [readIds, setReadIds] = useState<Set<number>>(new Set());
  const loadingRef = useRef(false);

  const { data } = useGetProjectNotificationsQuery(page);
  const [markRead] = useMarkNotificationReadMutation();

  useEffect(() => {
    if (!data) return;
    setItems((prev) => {
      if (data.page === 1) return data.rows;
      // Merge, de-duping by id (Backbone fetchPage uses merge/add).
      const seen = new Set(prev.map((p) => p.id));
      return [...prev, ...data.rows.filter((r) => !seen.has(r.id))];
    });
    loadingRef.current = false;
  }, [data]);

  const ulRef = useRef<HTMLUListElement | null>(null);

  const onScroll = useCallback(() => {
    const el = ulRef.current;
    if (!el) return;
    const bottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (bottom > 150 || loadingRef.current || page * PAGE_SIZE > items.length) return;
    loadingRef.current = true;
    setPage((p) => p + 1);
  }, [page, items.length]);

  const isUnread = useCallback(
    (n: IProjectNotification) => !n.is_read && !readIds.has(n.id),
    [readIds]
  );

  const hasUnread = items.some((n) => isUnread(n));
  const empty = items.length < 1;

  const onRowClick = useCallback(
    (n: IProjectNotification) => {
      if (!isUnread(n)) return;
      setReadIds((s) => new Set(s).add(n.id));
      markRead({ id: n.id });
      // new_project rows: clear the .unread-notification highlight on the row.
      const type = n.is_critical_task_started ? 'critical_task' : 'new_project';
      if (type === 'new_project' && n.project_id != null) {
        dispatch(clearUnreadProject(n.project_id));
      }
    },
    [isUnread, markRead, dispatch]
  );

  const onRowOpen = useCallback((n: IProjectNotification) => {
    if (n.project_id == null) return;
    window.open(`/project/${n.project_id}#project`, '_blank', 'noreferrer');
  }, []);

  return (
    <div id="notifications_wrapper" className={empty ? 'non-clickable' : undefined}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        className={`with-padding-2 right-border notifications with-list ka-has-tooltip${
          hasUnread ? ' active' : ''
        }`}
        tabIndex={-1}
        data-for="notifications_list"
        tooltip="Notifications"
        role="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (empty) return;
          onToggle();
        }}
      >
        <i className="material-symbols-outlined">&#xe004;</i>
      </a>
      <div
        id="notifications_list"
        className={`nav-list${open && !empty ? '' : ' hide'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="notifications-heading">Notifications</div>
        <ul id="notifications_ul" ref={ulRef} onScroll={onScroll}>
          <li className="title-block-li notifications_li list-li-row">
            <span className="desc">PROJECT</span>
            <span className="type">ACTION</span>
            <span className="initials">BY</span>
            <span className="date">DATE &amp; TIME</span>
          </li>
          {items.map((n) => {
            const unread = isUnread(n);
            const type = n.is_critical_task_started ? 'critical_task' : 'new_project';
            return (
              <li
                key={n.id}
                id={`notifications_${n.id}`}
                className={`notifications_li list-li-row ${unread ? 'unread' : 'read'}`}
                data-id={n.id}
                data-project-id={n.project_id}
                data-type={type}
                onClick={() => onRowClick(n)}
                onDoubleClick={() => onRowOpen(n)}
              >
                <span className="desc" tooltip={n.project_name}>
                  {n.project_name}
                </span>
                <span className="type">
                  {n.is_critical_task_started
                    ? `${n.critical_task ?? ''} task started`
                    : 'Created new project'}
                </span>
                <span className="initials">{n.initials}</span>
                <span className="date">{n.create_date}</span>
              </li>
            );
          })}
        </ul>
        <div className="close-btn-wrapper">
          <button
            id="notifications-close-btn"
            className="btn close-list"
            data-for="notifications_list"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsDropdown;
